"use client";

import { useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { useAni } from "@/context/AniContext";
import { useModulePermissions } from "@/context/ModulePermissionsContext";
import { useInAppAgentProfile } from "@/hooks/useInAppAgentProfile";
import { buildStandupBrief, mapMeetingSurfaceCardToHomeMeeting } from "@/components/agentic-surface/adapters";
import type { MissionControlFeedStats } from "@/services/api/agentic-v2/types";
import { usePrimaryHomeSurface } from "@/components/agentic-surface/useAgenticSurface";
import { buildHomeStats } from "@/components/home/homeStats";
import { useHomeSurfaceData } from "@/components/home/useHomeSurfaceData";
import { OPENWORK_ADVISOR_SURFACE_ID } from "@/components/home/resolveHomeSurfaceId";
import { buildOpenworkStandupBrief } from "@/components/home/standup/openworkStandupBrief";
import {
  OPENWORK_HOME_REFRESH_EVENT,
  configureOpenworkDemoStorage,
} from "@/components/review/openworkDemoState";
import { normalizeOpenworkLetterReviewQueue } from "@/components/review/reviewDemoHelpers";
import { setInAppAgentPriorityTaskId } from "@/services/agent";
import {
  buildOpenworkInAppAgentContext,
  buildReviewQueueInAppAgentContext,
} from "@/services/inAppAgent/buildInAppAgentRuntimeContext";
import { publishOpenworkAgentContext } from "@/services/inAppAgent/openworkAgentContextBridge";
import { resolveAdviserPersonaName } from "@/utils/adviserPersonaName";

/** Keeps Floating Ani standup context in sync when Home is not open. */
export function ReviewQueueInAppAgentContextLayer() {
  const { user } = useAuth();
  const { setApiData } = useAni();
  const { hasPermission } = useModulePermissions();
  const companyId = (user?.company_id ?? "").toLowerCase();
  const isOpenwork = companyId === "openworks";
  const isSaltus = companyId === "saltus";
  const enabled = isOpenwork || isSaltus;
  const isAdminRole =
    user?.role === "admin" || user?.role === "super_admin" || user?.role === "manager";
  const canUseAni = hasPermission("ani");

  useInAppAgentProfile(enabled && canUseAni ? user?.company_id : undefined);

  const { surfaceId: saltusSurfaceId } = usePrimaryHomeSurface();
  const openworkSurfaceId = OPENWORK_ADVISOR_SURFACE_ID;
  const surfaceId = isOpenwork ? openworkSurfaceId : saltusSurfaceId ?? "saltus_advisor";
  const surfaceVisibility = isAdminRole ? "all" : "me";

  useEffect(() => {
    if (!isOpenwork) return;
    configureOpenworkDemoStorage(user?.email ?? user?.id ?? null);
  }, [isOpenwork, user?.email, user?.id]);

  const { data, loading, refreshAll } = useHomeSurfaceData({
    surfaceId,
    enabled,
    requireWindowLive: false,
    visibility: surfaceVisibility,
  });

  useEffect(() => {
    if (!isOpenwork) return;
    const onRefresh = () => {
      void refreshAll();
    };
    window.addEventListener("openwork-demo-reseeded", onRefresh);
    window.addEventListener(OPENWORK_HOME_REFRESH_EVENT, onRefresh);
    return () => {
      window.removeEventListener("openwork-demo-reseeded", onRefresh);
      window.removeEventListener(OPENWORK_HOME_REFRESH_EVENT, onRefresh);
    };
  }, [isOpenwork, refreshAll]);

  const personaName = useMemo(() => resolveAdviserPersonaName(user), [user]);

  const pendingReviewTasks = useMemo(() => {
    const raw = data?.needsMeTasks ?? [];
    if (isOpenwork) {
      return normalizeOpenworkLetterReviewQueue(raw, { assigneeEmail: user?.email });
    }
    return raw;
  }, [data?.needsMeTasks, isOpenwork, user?.email]);

  const meetings = useMemo(() => {
    const cards = [...(data?.rawSnapshot?.meetings ?? [])].sort((a, b) => {
      const left = a.scheduled_at ? new Date(a.scheduled_at).getTime() : Number.POSITIVE_INFINITY;
      const right = b.scheduled_at ? new Date(b.scheduled_at).getTime() : Number.POSITIVE_INFINITY;
      return left - right;
    });
    return cards.map(mapMeetingSurfaceCardToHomeMeeting);
  }, [data?.rawSnapshot?.meetings]);

  const openworkBriefing = useMemo(() => {
    if (!isOpenwork) return null;
    const nextMeeting = meetings[0];
    return buildOpenworkStandupBrief(personaName, pendingReviewTasks, loading, {
      allLettersApproved: pendingReviewTasks.length === 0,
      approvedCount: 0,
      nextMeeting: nextMeeting
        ? {
            clientName: nextMeeting.clientName,
            title: nextMeeting.title ?? "Client meeting",
            dateLabel: nextMeeting.dateLabel,
            time: nextMeeting.time,
          }
        : null,
    });
  }, [isOpenwork, personaName, pendingReviewTasks, loading, meetings]);

  const saltusBriefing = useMemo(() => {
    if (!isSaltus) return null;
    const homeTasks = data?.homeTasks ?? [];
    const inProgressQueueTasks = data?.inProgressQueueTasks ?? [];
    const homeStats = buildHomeStats(homeTasks, []);
    const feedStats: MissionControlFeedStats | null | undefined =
      pendingReviewTasks.length > 0
        ? {
            artifacts_produced_24h: data?.rawSnapshot?.feed_stats?.artifacts_produced_24h ?? 0,
            missions_started_24h: data?.rawSnapshot?.feed_stats?.missions_started_24h ?? 0,
            high_priority_completed_24h: data?.rawSnapshot?.feed_stats?.high_priority_completed_24h ?? 0,
            needs_attention_now: pendingReviewTasks.length,
          }
        : data?.rawSnapshot?.feed_stats ?? undefined;
    const narrative = buildStandupBrief(
      homeTasks,
      homeStats,
      personaName,
      feedStats,
      inProgressQueueTasks,
    );
    const count = pendingReviewTasks.length;
    return {
      greetingLine: `Hi ${personaName}.`,
      leadLine:
        count > 0
          ? `${count} onboarding task${count === 1 ? "" : "s"} need you.`
          : "Your onboarding queue is clear.",
      narrative,
    };
  }, [
    isSaltus,
    data?.homeTasks,
    data?.inProgressQueueTasks,
    data?.rawSnapshot?.feed_stats,
    personaName,
    pendingReviewTasks.length,
  ]);

  useEffect(() => {
    if (!enabled) return;
    if (isOpenwork && !openworkBriefing) return;
    if (isSaltus && !saltusBriefing) return;

    const nextMeeting = meetings[0];
    const context = isOpenwork
      ? buildOpenworkInAppAgentContext({
          companyId: user?.company_id,
          surfaceId,
          briefing: openworkBriefing!,
          reviewTasks: pendingReviewTasks,
          assigneeEmail: user?.email,
          nextMeeting: nextMeeting
            ? {
                clientName: nextMeeting.clientName,
                title: nextMeeting.title ?? "Client meeting",
                dateLabel: nextMeeting.dateLabel,
                time: nextMeeting.time,
              }
            : null,
        })
      : buildReviewQueueInAppAgentContext({
          companyId: user?.company_id,
          surfaceId,
          briefing: saltusBriefing!,
          reviewTasks: pendingReviewTasks,
        });

    setApiData({ in_app_agent: context });
    publishOpenworkAgentContext(context);
    const priorityId = context.standup?.priority_task_id ?? undefined;
    setInAppAgentPriorityTaskId(priorityId);
  }, [
    enabled,
    isOpenwork,
    openworkBriefing,
    saltusBriefing,
    pendingReviewTasks,
    user?.company_id,
    surfaceId,
    setApiData,
    meetings,
    user?.email,
    isSaltus,
  ]);

  return null;
}
